import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { LanguageSelector } from './LanguageSelector';
import { useCart } from '../context/CartContext';
import { trackEvent } from '../seo/analytics';

export const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getCartCount } = useCart();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const cartCount = getCartCount();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_tire-wizard/artifacts/h82o40wz_file_000000004bdc71f59adfe6d7fae65f85.png"
              alt="Vargas Tire Truck Repair"
              className="h-16 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4 rtl:space-x-reverse">
            <LanguageSelector />
            <button
              onClick={() => navigate('/cart')}
              className="relative text-gray-700 hover:text-red-600 font-medium transition-colors px-3 py-2"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-gray-700 hover:text-red-600 font-medium transition-colors"
            >
              {t('header.services')}
            </button>
            <button
              onClick={() => scrollToSection('tires')}
              className="text-gray-700 hover:text-red-600 font-medium transition-colors"
            >
              {t('header.tires')}
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="text-gray-700 hover:text-red-600 font-medium transition-colors"
            >
              {t('header.testimonials')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-red-600 font-medium transition-colors"
            >
              {t('header.contact')}
            </button>
            <button
              onClick={() => navigate('/blog')}
              className="text-gray-700 hover:text-red-600 font-medium transition-colors"
              data-testid="nav-blog-desktop"
            >
              Blog
            </button>
            <button
              onClick={() => navigate('/blog')}
              className="text-gray-700 hover:text-red-600 font-medium transition-colors"
              data-testid="nav-car-care-tips-desktop"
            >
              Car Care Tips
            </button>
            <a
              href="/roadside"
              onClick={() =>
                trackEvent('roadside_request_started', {
                  event_category: 'engagement',
                  event_label: 'header_emergency_desktop',
                })
              }
            >
              <Button className="bg-orange-600 hover:bg-orange-700 text-white animate-pulse">
                🚨 {t('header.emergency')}
              </Button>
            </a>
            <a
              href="tel:3057724150"
              className="phone-number"
              onClick={() =>
                trackEvent('generate_lead', {
                  event_category: 'engagement',
                  event_label: 'header_call_now_desktop',
                  phone_number: '305-772-4150',
                })
              }
            >
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                <Phone className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                {t('header.callNow')}
              </Button>
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-gray-700"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              <div className="flex justify-center">
                <LanguageSelector />
              </div>
              <a
                href="/roadside"
                className="inline-block"
                onClick={() =>
                  trackEvent('roadside_request_started', {
                    event_category: 'engagement',
                    event_label: 'header_emergency_mobile',
                  })
                }
              >
                <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white animate-pulse">
                  🚨 {t('header.emergency')}
                </Button>
              </a>
              <button
                onClick={() => scrollToSection('services')}
                className="text-left rtl:text-right text-gray-700 hover:text-red-600 font-medium transition-colors"
              >
                {t('header.services')}
              </button>
              <button
                onClick={() => scrollToSection('tires')}
                className="text-left rtl:text-right text-gray-700 hover:text-red-600 font-medium transition-colors"
              >
                {t('header.tires')}
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className="text-left rtl:text-right text-gray-700 hover:text-red-600 font-medium transition-colors"
              >
                {t('header.testimonials')}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-left rtl:text-right text-gray-700 hover:text-red-600 font-medium transition-colors"
              >
                {t('header.contact')}
              </button>
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate('/blog');
                }}
                className="text-left rtl:text-right text-gray-700 hover:text-red-600 font-medium transition-colors"
                data-testid="nav-blog-mobile"
              >
                Blog
              </button>
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate('/blog');
                }}
                className="text-left rtl:text-right text-gray-700 hover:text-red-600 font-medium transition-colors"
                data-testid="nav-car-care-tips-mobile"
              >
                Car Care Tips
              </button>
              <a
                href="tel:3057724150"
                className="inline-block phone-number"
                onClick={() =>
                  trackEvent('generate_lead', {
                    event_category: 'engagement',
                    event_label: 'header_call_now_mobile',
                    phone_number: '305-772-4150',
                  })
                }
              >
                <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
                  <Phone className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
                  {t('header.callNow')}
                </Button>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};